import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';

const siteUrl = 'https://tj-yiya.cn';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
    .slice(0, 3);

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: { '@type': 'Organization', name: article.author },
    datePublished: article.publishDate,
    publisher: { '@type': 'Organization', name: '樱花视频' },
    url: articleUrl,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${siteUrl}/category/${categories.find((c) => c.id === article.categoryId)?.slug || ''}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
    ],
  };

  return (
    <>
      <SEOHelmet
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.coverImage}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-end px-4 pb-12 text-center">
            <div className="max-w-3xl">
              <div className="flex items-center justify-center gap-3 mb-4 text-xs text-white/70 flex-wrap">
                <span className="px-2.5 py-1 bg-primary-500/80 text-background-50 rounded-full font-medium">
                  {article.categoryName}
                </span>
                <span>{article.publishDate}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                </span>
                <span>{article.readTime}</span>
              </div>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-white mb-3 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-white/60">作者：{article.author}</p>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${categories.find((c) => c.id === article.categoryId)?.slug || ''}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: article.content
                .replace(/<h2>/g, '<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">')
                .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 mb-4 leading-relaxed">'),
            }}
          />

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/60">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs text-foreground-500">{ra.publishDate}</span>
                        <span className="text-xs text-foreground-500">{ra.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}